import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useGetTopicList } from "../../api/services/client/clientApis";
import { ProjectKeyDetailsResponse } from "../../api/services/project/model";
import { useGetProjectActivitiesByProjectId } from "../../api/services/project/projectApis";
import { BILLING_DETAILS_SITE_CORE_VALUE } from "../../helpers/constants";
import {
  checkValueIsZero,
  getValueOrBlankArray,
  isEmpty,
} from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import { keyDetailsSelector } from "../../redux/slices/project/projectSelector";
import { BillingTypeConfigureTableProps } from "./BillingTypeConfigureTable";
import {
  addEntriesToFormKey,
  BillingTypeCommonConfigParams,
  BillingTypeMilestoneConfigParams,
  BillingTypeRemainingAmountParams,
  BillingTypeScheduleConfigParams,
  copyBillingDataToLocal,
  sortFilterData,
  validateAmountWithRemainingValue,
  validateBillingTypeMilestone,
  validateBillingTypeScheduleBase,
  validatePercentageWithRemainingValue,
} from "./helper";
import {
  BillingTypeMilestoneConfigureTableData,
  BillingTypeScheduleConfigureTableData,
} from "./modelsAndSchemas/Models";

export const useBillingTypeConfigureTableHook = (
  props: BillingTypeConfigureTableProps,
) => {
  const {
    forceUpdate,
    programTemplateId,
    isInEditMode,
    clientId,
    billingConfigurationDetailsFormKeys,
    isFromProject = false,
  } = props;
  const { getValues, setValue, setError, clearErrors, watch } =
    useFormContext();
  const {
    fetchValidationMessage,
    fetchDropdownValues,
    commonBillingConfigurationDetails,
    commonLabels,
  } = useSiteCore();

  const { id: projectId } =
    useSelector(keyDetailsSelector) || ({} as ProjectKeyDetailsResponse);

  const [filteredResults, setFilteredResults] = useState<
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[]
  >([]);

  const {
    data: programTopics,
    refetchWithParams: getTopicList,
    clearListOfTopic,
  } = useGetTopicList();

  const {
    data: projectTopicList,
    refetchWithParams: getProjectTopicList,
    clearListOfProjectTopic,
  } = useGetProjectActivitiesByProjectId();

  type EntryType =
    | BillingTypeScheduleConfigureTableData
    | BillingTypeMilestoneConfigureTableData
    | null;

  const [selectedEntryForEdit, setSelectedEntryForEdit] =
    useState<EntryType>(null);

  const [onDataAddedCallback, setOnDataAddedCallback] =
    useState<boolean>(false);
  const [onDataDeletedCallback, setOnDataDeletedCallback] =
    useState<boolean>(false);
  const [
    showDeleteScheduleOrMilestoneModal,
    setShowDeleteScheduleOrMilestoneModal,
  ] = useState(false);
  const selectedDeleteScheduleOrMilestoneRef = useRef<
    | BillingTypeScheduleConfigureTableData
    | BillingTypeMilestoneConfigureTableData
  >();

  const {
    billingTypeIdKey,
    schedulePeriodKey,
    feeKey,
    scheduleKey,
    feePercentageKey,
    amountWithFormatKey,
    amountKey,
    scheduleFrequencyKey,
    milestoneKey,
    activitiesKey,
    billingSchedulesKey,
    feePaymentTypeKey,
  } = billingConfigurationDetailsFormKeys;

  const watchBillingSchedulesKey = useWatch({
    name: billingSchedulesKey,
  });

  const {
    pleaseFillProgramFeeErrorMessage,
    pleaseFillProjectFeeErrorMessage,
    totalAmountMustEqualProgramFeeErrorMessage,
    totalAmountMustEqualProjectFeeErrorMessage,
    areYouSureYouWantToDeleteTheSchedule,
    areYouSureYouWantToDeleteTheMilestone,
  } = commonBillingConfigurationDetails;

  useEffect(() => {
    if (projectId) {
      getProjectTopicList(projectId, true);
    } else clearListOfProjectTopic();
  }, [projectId]);

  useEffect(() => {
    if (clientId && programTemplateId) {
      getTopicList(clientId, programTemplateId);
    } else clearListOfTopic();
  }, [clientId, programTemplateId]);

  const topicsList = useMemo(() => {
    if (isFromProject)
      return projectTopicList?.length
        ? projectTopicList.map((item) => item.topic)
        : [];
    else return getValueOrBlankArray(programTopics);
  }, [programTopics, projectTopicList]);

  const handleBillingConfigurationDeleteClick = (
    row:
      | BillingTypeScheduleConfigureTableData
      | BillingTypeMilestoneConfigureTableData,
  ) => {
    const filteredResultsCopy = [...filteredResults];
    const index = filteredResultsCopy.findIndex((item) => {
      return item.description === row.description;
    });
    filteredResultsCopy.splice(index, 1);
    const filteredResultsCopySorted = isBillingTypeScheduleBase()
      ? (filteredResultsCopy as BillingTypeScheduleConfigureTableData[])
      : (filteredResultsCopy as BillingTypeMilestoneConfigureTableData[]);
    sortFilterData(filteredResultsCopySorted);
    setFilteredResults(filteredResultsCopySorted);
    addEntriesToFormKey(
      billingSchedulesKey,
      setValue,
      filteredResultsCopySorted,
      forceUpdate,
    );
    setOnDataDeletedCallback(!onDataDeletedCallback);
  };

  useEffect(() => {
    const billingSchedulesValue = getValues(billingSchedulesKey);

    if (isEmpty(billingSchedulesValue)) {
      setFilteredResults([]);
      return;
    }
    copyBillingDataToLocal(
      isBillingTypeScheduleBase(),
      billingSchedulesValue,
      filteredResults,
      billingSchedulesKey,
      setValue,
      setFilteredResults,
      forceUpdate,
    );
  }, [watchBillingSchedulesKey]);

  useEffect(() => {
    checkIfFeeGiven();
  }, [watch(feeKey)]);

  const checkIfFeeGiven = () => {
    const fee = getValues(feeKey);

    if (isEmpty(fee)) {
      setError(
        isValueTypePercentage() ? feePercentageKey : amountWithFormatKey,
        {
          type: "custom",
          message: isFromProject
            ? pleaseFillProjectFeeErrorMessage
            : pleaseFillProgramFeeErrorMessage,
        },
      );
      return false;
    } else if (checkValueIsZero(fee)) {
      return false;
    }
    clearErrors(amountWithFormatKey);
    return true;
  };

  const checkValidations = () => {
    const commonConfigParams: BillingTypeCommonConfigParams = {
      isSchedulePeriodMonthly: isSchedulePeriodMonthly(),
      isValueTypePercentage: isValueTypePercentage(),
      feeKey,
      feePercentageKey,
      amountWithFormatKey,
      amountKey,
      isFromProject,
      getValues,
      setError,
      clearErrors,
    };

    const scheduleConfigParams: BillingTypeScheduleConfigParams = {
      ...commonConfigParams,
      isBillingTypeScheduleBase: isBillingTypeScheduleBase(),
      scheduleKey,
      scheduleFrequencyKey,
    };

    const milestoneConfigParams: BillingTypeMilestoneConfigParams = {
      ...commonConfigParams,
      isBillingTypeMilestoneBase: isBillingTypeMilestoneBase(),
      milestoneKey,
      activitiesKey,
      topicsList,
    };

    return (
      validateBillingTypeScheduleBase(
        filteredResults,
        selectedEntryForEdit,
        scheduleConfigParams,
        fetchValidationMessage,
        fetchDropdownValues,
        commonBillingConfigurationDetails,
      ) ||
      validateBillingTypeMilestone(
        filteredResults,
        selectedEntryForEdit,
        milestoneConfigParams,
        fetchValidationMessage,
        commonBillingConfigurationDetails,
      )
    );
  };

  useEffect(() => {
    const commonConfigParams: BillingTypeRemainingAmountParams = {
      feeKey,
      amountKey,
      amountWithFormatKey,
      isFromProject,
      getValues,
      setError,
      clearErrors,
    };

    validateAmountWithRemainingValue(
      filteredResults,
      selectedEntryForEdit,
      commonConfigParams,
      totalAmountMustEqualProgramFeeErrorMessage,
      totalAmountMustEqualProjectFeeErrorMessage,
    );
  }, [watch(amountWithFormatKey)]);

  useEffect(() => {
    validatePercentageWithRemainingValue(
      filteredResults,
      selectedEntryForEdit,
      feePercentageKey,
      commonBillingConfigurationDetails.totalPercentageCannotExceed100,
      getValues,
      setError,
      clearErrors,
    );
  }, [watch(feePercentageKey)]);

  const isBillingTypeScheduleBase = () => {
    const billingType = getValues(billingTypeIdKey);
    return billingType === BILLING_DETAILS_SITE_CORE_VALUE.billingTypeSchedule;
  };
  const isBillingTypeMilestoneBase = () => {
    const billingType = getValues(billingTypeIdKey);
    return billingType === BILLING_DETAILS_SITE_CORE_VALUE.billingTypeMilestone;
  };

  const isValueTypePercentage = () => {
    const feePaymentTypeValue = getValues(feePaymentTypeKey);
    return (
      feePaymentTypeValue === BILLING_DETAILS_SITE_CORE_VALUE.valuePercentage
    );
  };

  const addBillingConfigurationScheduleOrMilestone = () => {
    let newSchedule:
      | BillingTypeScheduleConfigureTableData
      | BillingTypeMilestoneConfigureTableData
      | null = null;
    newSchedule = isBillingTypeScheduleBase()
      ? (checkValidations() as BillingTypeScheduleConfigureTableData)
      : (checkValidations() as BillingTypeMilestoneConfigureTableData);

    if (newSchedule && checkIfFeeGiven()) {
      clearInputValues();
      const updatedResults = [...filteredResults, newSchedule];
      const sortedResults = isBillingTypeScheduleBase()
        ? (updatedResults as BillingTypeScheduleConfigureTableData[])
        : (updatedResults as BillingTypeMilestoneConfigureTableData[]);
      sortFilterData(sortedResults);
      setFilteredResults(sortedResults);
      addEntriesToFormKey(
        billingSchedulesKey,
        setValue,
        sortedResults,
        forceUpdate,
      );
      setOnDataAddedCallback(!onDataAddedCallback);
    }
  };

  const updateBillingConfigurationScheduleOrMilestone = () => {
    const updateSchedule = isBillingTypeScheduleBase()
      ? (checkValidations() as BillingTypeScheduleConfigureTableData)
      : (checkValidations() as BillingTypeMilestoneConfigureTableData);

    if (!updateSchedule || !checkIfFeeGiven()) return false;

    clearInputValues();

    const updatedResults = filteredResults.map((item) =>
      updateSchedule.description === item.description ? updateSchedule : item,
    );

    const sortedResults = isBillingTypeScheduleBase()
      ? (updatedResults as BillingTypeScheduleConfigureTableData[])
      : (updatedResults as BillingTypeMilestoneConfigureTableData[]);

    sortFilterData(sortedResults);
    setFilteredResults(sortedResults);
    addEntriesToFormKey(
      billingSchedulesKey,
      setValue,
      sortedResults,
      forceUpdate,
    );
    setOnDataAddedCallback(!onDataAddedCallback);
    onCancelEditFlow();
    return true;
  };

  const isSchedulePeriodMonthly = () => {
    const schedulePeriod = getValues(schedulePeriodKey);
    return (
      schedulePeriod === BILLING_DETAILS_SITE_CORE_VALUE.schedulePeriodMonthly
    );
  };

  const clearAllValues = () => {
    setFilteredResults([]);
    clearInputValues();
    setValue(billingSchedulesKey, []);
  };

  const clearInputValues = () => {
    setValue(scheduleKey, "");
    setValue(feePercentageKey, "");
    setValue(amountKey, "");
    setValue(amountWithFormatKey, "");
    setValue(scheduleFrequencyKey, "");
    setValue(milestoneKey, "");
    setValue(activitiesKey, "");
  };

  const onCancelEditFlow = () => {
    setSelectedEntryForEdit(null);
  };

  const handleModalClose = () => {
    if (showDeleteScheduleOrMilestoneModal) {
      setShowDeleteScheduleOrMilestoneModal(false);
    }
  };

  const handleDeleteScheduleOrMilestoneClick = () => {
    const selectedScheduleOrMilestone =
      selectedDeleteScheduleOrMilestoneRef.current;
    if (selectedScheduleOrMilestone) {
      setShowDeleteScheduleOrMilestoneModal(false);
      handleBillingConfigurationDeleteClick(selectedScheduleOrMilestone);
    }
  };

  const deleteScheduleOrMilestoneAction = (
    row:
      | BillingTypeScheduleConfigureTableData
      | BillingTypeMilestoneConfigureTableData,
  ) => {
    if (!showDeleteScheduleOrMilestoneModal) {
      selectedDeleteScheduleOrMilestoneRef.current = row;
      setShowDeleteScheduleOrMilestoneModal(true);
    }
  };

  const handleScheduleOrMilestoneConfigurationAction = (
    row:
      | BillingTypeScheduleConfigureTableData
      | BillingTypeMilestoneConfigureTableData,
  ) => {
    if (isInEditMode) {
      deleteScheduleOrMilestoneAction(row);
    } else {
      handleBillingConfigurationDeleteClick(row);
    }
  };

  return {
    handleScheduleOrMilestoneConfigurationAction,
    isBillingTypeScheduleBase,
    isBillingTypeMilestoneBase,
    isValueTypePercentage,
    isSchedulePeriodMonthly,
    billingConfigurationDetailsFormKeys,
    filteredResults,
    setFilteredResults,
    onDataAddedCallback,
    onDataDeletedCallback,
    showDeleteScheduleOrMilestoneModal,
    setShowDeleteScheduleOrMilestoneModal,
    selectedEntryForEdit,
    setSelectedEntryForEdit,
    addBillingConfigurationScheduleOrMilestone,
    updateBillingConfigurationScheduleOrMilestone,
    clearAllValues,
    clearInputValues,
    onCancelEditFlow,
    handleModalClose,
    handleDeleteScheduleOrMilestoneClick,
    checkIfFeeGiven,
    topicsList,
    commonLabels,
    areYouSureYouWantToDeleteTheSchedule,
    areYouSureYouWantToDeleteTheMilestone,
  };
};
